// Sugalarm — opens /ws/glucose and exposes the rolling state needed by
// the Now screen, the urgent overlay, and (cycle 14) the household
// dashboard.
//
// The Reading buffer is capped at 36 (3 hours of 5-minute Dexcom data).
// Bigger windows are page-fetched, not pushed.
//
// Per-frame routing rules (cycles 13 + 14):
//   - `snapshot` seeds `byMember` for every entry and the top-level
//     fields from the active member's row (fallback: first member with
//     a reading). The dashboard cards are seeded from the snapshot too.
//   - `glucose` always updates `byMember[member_id]` and the matching
//     dashboardCards row. It mirrors to the top-level
//     `lastReading` / `recentReadings` only when `member_id === activeMemberId`.
//   - `alert` always updates `byMember[member_id]` and the matching
//     dashboardCards row + the household-wide `unackCount`. It mirrors
//     to top-level `lastAlert` / `urgentActive` ONLY for the active
//     member — that's the wire-level enforcement of "audio alarms
//     only for the active member" (the urgent overlay and TTS code
//     both key off the top-level fields).
//   - `prediction` always updates `byMember`; mirrors to top-level
//     only for the active member.
//
// When the active member changes, top-level active-scoped fields are
// cleared and re-seeded via REST (`/api/glucose/latest` +
// `/api/glucose/history` + `membersApi.dashboard()`) so the dashboard
// and Now screen reflect the new member without waiting for the next
// push.

import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

import * as alertsApi from "../api/alerts";
import * as glucoseApi from "../api/glucose";
import * as membersApi from "../api/members";
import { isCapacitorNative } from "../lib/capacitor";
// Cycle 435: the per-member confirmation / verdict SHAPES only. The
// selector itself (`selectCurrentGlucose`) is never called here — the hook
// carries the facts, the display judges them.
import type { ServerVerdict, StreamConfirmation } from "../lib/currentGlucose";
import {
  accessTokenExpiresWithinMs,
  refreshIdentity,
  refreshSessionForSocket,
} from "../lib/apiClient";
import { getBearer } from "../lib/auth";
import { markVerifiedFresh, noteAppActive } from "../lib/appLifecycle";
import { SNAPSHOT_OPEN_ALERTS_LIMIT, type ServerRowTruth } from "../lib/episodeTruth";
import { ageSeconds, isStale } from "../lib/glucose";
import { asOfKey } from "../lib/serviceLock";
import {
  READING_CONFIRM_BOUND_MS,
  RESEED_TIMEOUT_MS,
} from "../lib/readingFreshness";
import { readingBoundMs as deriveReadingBoundMs } from "../lib/readingCadence";
import {
  WAKE_MIN_GAP_MS,
  createReseedRunner,
  createWakeHandler,
  type ReseedRunner,
} from "../lib/wakeReseed";
import {
  GlucoseSocket,
  noteReseedTimeout,
  shouldRefreshAfterRefusedOpen,
  type ConnectionStatus,
  type DeliveryMeta,
} from "../lib/ws";
import { callQuietKey } from "./useAlarmSounds";

// ─── Cycle 474: the return path's bounds ────────────────────────────────
/** A socket-driven session refresh at most this often. */
const SOCKET_REFRESH_MIN_GAP_MS = 30_000;
/** Refresh an access token that expires within this, on wake (the brief's
 *  60 s). */
const TOKEN_REFRESH_LEAD_MS = 60_000;
/** A reseed whose reading call failed is retried this many times in a row,
 *  this far apart, while visible. */
const RESEED_RETRY_MAX = 3;
const RESEED_RETRY_MS = 5_000;
import { notifyHeroRosterChanged } from "../lib/heroRosterSignal";
// Cycle 410: ONLY the anchor fold. `selectAudibleRow` is deliberately NOT
// imported here — this file decides the SLOT (`preferSoundingRow`), never
// which row's audio plays; a source guard pins the separation.
import {
  foldLeadAnchorAtReceipt,
  foldPendingAnchor,
  type LeadAnchor,
} from "../lib/audibleRow";
import type {
  ActiveBroadcast,
  Alert,
  AlertLevel,
  CustodyCheckinQuestion,
  Member,
  MemberWithLatest,
  Prediction,
  Reading,
  Reminder,
  ReminderFire,
  Treatment,
  UserSettings,
  WSFrame,
} from "../lib/types";

const TREATMENTS_CAP = 50;

const READINGS_CAP = 36;

// Cycle 112: live-fire event envelopes, distinct from the cached
// `lastAlert` / `lastPrediction`. A new event is emitted ONLY by a live
// `alert` / `prediction` WS frame for the active member — never by the
// connect-time snapshot seed, a reconnect/wake REST re-seed, or a
// state-refresh frame (heartbeat / snooze / silence / recovered / ack).
// One-shot alarm sounds key off these so a deploy-reload that re-seeds a
// stale unacked alert into `lastAlert` can't replay the sound. `seq` is a
// monotonic per-hook-instance counter; a consumer edge-detects a new
// event by `seq` (not by alert id), so the cycle-66 mild→urgent re-arm —
// which rebroadcasts the SAME alert id at a new level — correctly counts
// as a fresh event (new `seq`) and re-fires audio.
export interface AlertEvent {
  alert: Alert;
  seq: number;
}
/** Cycle 407: a `custody_checkin` frame for the ACTIVE member — the edge
 *  the treating screen SPEAKS on. Like `AlertEvent`, `seq` is monotonic so
 *  a redelivered frame (NOTIFY duplicate, reconnect replay) and a genuine
 *  new question are told apart by the consumer's own key, not by identity
 *  of this object. The persisted question itself rides the alert row. */
export interface CheckinEvent {
  alertId: string;
  memberId: string;
  question: CustodyCheckinQuestion;
  expiresAt: string;
  seq: number;
}
/** Cycle 407: WHY the active member's treating window just ended, taken
 *  from the `alert_treating` TEARDOWN frame's `custody_end_reason`, with
 *  the live value at that moment. The returned alarm shows it as a
 *  one-line reason for a short while — a hand-back, never a silent swap. */
export interface CustodyHandBack {
  alertId: string;
  memberId: string;
  reason: string;
  sgv: number | null;
  atMs: number;
}
export interface PredictionEvent {
  prediction: Prediction;
  seq: number;
}

export interface MemberStreamState {
  lastReading: Reading | null;
  lastAlert: Alert | null;
  unackCount: number;
  hasUnackedUrgent: boolean;
  /** Per-member treatment cache (cycle 24). Newest-first, capped at
   *  `TREATMENTS_CAP`. Frames seed the cache as they arrive; consumers
   *  may merge this into their own REST-seeded list keyed by id. */
  recentTreatments: Treatment[];
  /** Treatment ids that arrived via `treatment_deleted` frames. The
   *  REST seed may still hold them; consumers filter by this set. */
  deletedTreatmentIds: Set<string>;
  /** Cycle 410: every alert-family row this device has seen for the
   *  member, by id — the OPEN ones plus the most recent closed one — so
   *  the descent rule (`lib/audibleRow.ts`) can see the `low` sibling
   *  while the URGENT row holds the slot. AUDIO SELECTION ONLY: never read
   *  for the slot, the banner, the countdown or a POST target. Bounded by
   *  `ALERT_ROWS_CAP`. */
  alertRows: Record<string, Alert>;
  /** Cycle 410: per row, when THIS device first learned of a lead window
   *  and the earliest server deadline it was told — the receipt-anchored
   *  cap that makes a bogus far-future `audio_lead_until` harmless. A row
   *  whose latest frame carries `null` has no entry: the end signal. */
  leadAnchors: Record<string, LeadAnchor>;
  /** Cycle 410.1: per row, the PENDING GRACE anchor (the crossing frame's
   *  transient `audio_lead_pending` + this device's receipt time). Any
   *  later frame for the row without the field RESOLVES it — the entry is
   *  dropped — so a snapshot (which never carries it) resolves loud. */
  pendingAnchors: Record<string, LeadAnchor>;
  /** ⚠ Cycle 435: THE SERVER'S LATEST STATEMENT ABOUT EACH ROW THIS DEVICE
   *  HAS EVER BOUND OR SEEN — `open` per the server's own sibling rule
   *  (fired, not recovered, not silenced), keyed by row id. Upserted from
   *  every alert-family frame, every REST `seed-alerts` row, and every
   *  snapshot; and on a SNAPSHOT every stored LOW-family row the snapshot
   *  does not list among `open_alerts` / `latest_alert` is marked CLOSED,
   *  because the snapshot's low-family open set is complete. This is what
   *  lets `useAlarmEpisode` tear down an episode whose row the server has
   *  closed while this device was frozen — the recovery stamp it used to
   *  wait for rode a frame that a dead socket never delivered, and the slot
   *  had already moved to another row. It CLEARS client state only; the
   *  client never recovers a row. Never read for the slot, the audio, a
   *  banner or a POST target. */
  serverRowTruth: Record<string, ServerRowTruth>;
}

export type { ServerRowTruth } from "../lib/episodeTruth";

export interface DashboardCard {
  member_id: string;
  display_name: string;
  relationship_label: string | null;
  is_self: boolean;
  cgm_provider: string | null;
  latest_reading: Reading | null;
  stale: boolean;
  age_seconds: number | null;
  current_alert_level: AlertLevel | null;
  /** Cycle 468: the member's owner is locked out; the row carries no
   *  reading. Folded into `serviceLockedByMember` on seed. */
  service_locked?: boolean;
}

interface State {
  status: ConnectionStatus;
  snapshotReceived: boolean;
  activeMemberId: string | null;
  // Active-member-scoped (top-level) fields — what the Now screen,
  // urgent overlay, KioskHeader mini value, and prediction pill all
  // read from. Mirroring to these is gated by active-member match
  // for glucose / alert / prediction frames.
  lastReading: Reading | null;
  recentReadings: Reading[]; // chronological, oldest first
  /** Cycle 362: un-acknowledged reminder occurrences, newest first, held as
   *  server truth. Seeded WHOLESALE from every connect-time `snapshot` (so a
   *  reconnect / wake-refresh / reboot converges instead of trusting local
   *  state), then kept live by `reminder_fired` / `reminder_dismissed`.
   *  User-scoped — deliberately NOT cleared on an active-member switch. */
  outstandingReminders: ReminderFire[];
  lastAlert: Alert | null;
  lastPrediction: Prediction | null;
  // Cycle 112: live-fire event fields — set ONLY by a live `alert` /
  // `prediction` frame for the active member, never by a seed/refresh.
  // Drive the one-shot alarm sounds (see useAlarmSounds). `eventSeq` is
  // the shared monotonic counter both events draw their `seq` from.
  lastAlertEvent: AlertEvent | null;
  // Cycle 407: the check-in edge for the active member (speech trigger)
  // and the last teardown reason (hand-back line). Both active-scoped,
  // cleared on member switch like every other active-scoped field.
  lastCheckinEvent: CheckinEvent | null;
  custodyHandBack: CustodyHandBack | null;
  lastPredictionEvent: PredictionEvent | null;
  eventSeq: number;
  // Household-wide.
  unackCount: number;
  urgentActive: boolean;
  // Cycle 381: absolute epoch-ms deadline, per member, during which the
  // PHONE's in-app alarm cue stays quiet because a voice call was just
  // dialed. A DEADLINE, never a flag — see VoiceCallPlacedFrame.
  voiceCallQuietUntilByMember: Record<string, number>;
  // Cycle 403: absolute epoch-ms deadline during which this account's
  // mobile / web sessions cut in-app alarm audio because we are on the
  // phone with that person right now. `emergency_contacts.linked_user_id`
  // is the join that lets a frame name the one person being called.
  // Cycle 406: keyed by `callQuietKey(user_id, member_id)` — account AND
  // member — so a call about the member never quiets Ben's alarm on the same
  // handset. A DEADLINE, never a flag; end-of-call arrives as a second
  // frame whose `until` is already past. See CallQuietFrame.
  callQuietUntilByUser: Record<string, number>;
  // Per-member maps for the household dashboard.
  byMember: Record<string, MemberStreamState>;
  dashboardCards: DashboardCard[];
  // Cycle 88: per-member CGM credential-failure state. Seeded from the
  // connect-time snapshot, updated by `cgm_auth` frames. The active
  // member's value drives the "update credentials" modal.
  cgmAuthFailedByMember: Record<string, boolean>;
  // Cycle 468: per-member "monitoring suspended by the subscription lock".
  // Seeded by the connect-time `service_state` frames and the dashboard
  // rows (`service_locked`), flipped by every lock / unlock frame. The
  // active member's value drives the follower card and silences the No
  // Data alarm for a locked member; the OWNER's own lockout reads
  // `GET /api/me/subscription`, never this map.
  serviceLockedByMember: Record<string, boolean>;
  // Cycle 468: the `as_of` of the newest `service_state` frame applied per
  // member (epoch ms), so a frame delivered late never overwrites a newer one.
  serviceLockedAsOfByMember: Record<string, number>;
  // Cycle 97: operator broadcast banner (not member-scoped). Set by an
  // `admin_broadcast` frame; cleared by a `cleared:true` frame for the
  // same id. Null = no active broadcast.
  activeBroadcast: ActiveBroadcast | null;
  // Cycle 429: how the LAST delivery arrived — when, and whether it was a
  // backlog / a (re)connect seed (`catchUp`). The one-shot cues read it: an
  // event cue never plays for a delivery that was applied as state, and
  // never for one that arrived longer ago than the cue age bound.
  lastFrameMeta: DeliveryMeta | null;
  // ⚠ CYCLE 433 — THE PROOF OF CURRENCY. Epoch ms of the last moment the
  // SERVER told this client what the newest reading is: a reading-bearing
  // frame (`snapshot` / `glucose`, any member — the question is whether
  // this device is still being told, not whose sensor it is) or a REST
  // reseed whose `/api/glucose/latest` answered. `null` = never. A pong
  // does NOT confirm: the 2026-09-13 socket was delivering nothing and the
  // number on screen was eight minutes stale. DISPLAY ONLY — see
  // `lib/readingFreshness.ts`; no rail predicate reads this field.
  readingConfirmedAtMs: number | null;
  /** ⚠ CYCLE 434 — the reading-age bound derived from the ACTIVE MEMBER's
   *  own feed (`lib/readingCadence.ts`), in ms. Cycle 433's constant until
   *  enough inter-reading gaps have been observed. DISPLAY AND TRANSPORT
   *  ONLY: it decides when this client stops trusting its own copy and
   *  goes to ask. The SERVER decides what is stale for alarm purposes. */
  readingBoundMs: number;
  /** The age past which a reading may no longer be presented as current on
   *  this member's feed: the bound plus one reseed allowance. */
  readingUnknownAfterMs: number;
  // ⚠ Both fields are DERIVED in the hook's memo, not written by any
  // reducer action; they sit on the state type because every consumer
  // types the socket as `GlucoseSocketState`, and `initialState` carries
  // the fallback so a component that renders before the first reading is
  // never handed `undefined`.
  /** ⚠ CYCLE 435 — CONFIRMATION IS PER MEMBER, NOT TRANSPORT-WIDE. The
   *  last server-authored statement about EACH member's newest reading:
   *  a snapshot row for that member, a `glucose` frame that is the newest
   *  applied reading for that member, or the ACTIVE member's REST reseed
   *  that answered. A frame for member A never confirms member B. Nothing
   *  else writes here — not a pong, not an alert frame, not a snooze or
   *  treating response, not the dashboard seed. `readingConfirmedAtMs`
   *  above stays as the transport-wide fact cycle 433 introduced (its
   *  watchdog and its tests read it); the DISPLAY reads this map through
   *  `lib/currentGlucose.ts` and nothing else. */
  confirmationByMember: Record<string, StreamConfirmation>;
  /** ⚠ CYCLE 435 — THE SERVER'S OWN VERDICT, CARRIED, NOT DROPPED. Every
   *  path that delivers a reading with `stale` / `age_seconds` (the
   *  snapshot rows, `/api/glucose/latest`) now keeps it, keyed by member
   *  and stamped with the reading it judged. A `glucose` frame carries no
   *  verdict and CLEARS the member's entry — the server said nothing about
   *  that delivery, so only the client's own age check applies to it. */
  verdictByMember: Record<string, ServerVerdict>;
}

type Action =
  | { type: "status"; status: ConnectionStatus }
  // `meta` is optional on the legacy single-frame action: absent = live.
  | { type: "frame"; frame: WSFrame; meta?: DeliveryMeta }
  // Cycle 429: a BATCH of frames with its delivery meta. Applied in order;
  // when `meta.catchUp` is set every frame is state and no event is
  // minted (see the `frame` case).
  | { type: "frames"; frames: WSFrame[]; meta: DeliveryMeta }
  // Cycle 429: the wake reseed's alert rows for the ACTIVE member (REST,
  // `GET /api/alerts`). The slot is rebound from SERVER rows only — never
  // from the locally remembered row.
  | {
      type: "seed-alerts";
      alerts: Alert[];
      /** Cycle 435: the member the reseed was STARTED for. A REST answer
       *  that lands after a member switch is dropped by the reducer, never
       *  attributed to whoever is active at reduce time. */
      memberId?: string | null;
    }
  // Cycle 435: a sign-in as a DIFFERENT account inside the mounted shell —
  // the per-member maps, cards and confirmations of the previous account
  // must not survive it. Never dispatched for a token ROTATION.
  | { type: "reset-account" }
  | { type: "ack-urgent" }
  | { type: "set-active"; memberId: string | null }
  // Cycle 433: `confirmedAtMs` is set ONLY when `/api/glucose/latest`
  // actually answered — a reseed whose reading call failed proves nothing
  // and must not renew the clock.
  | {
      type: "seed";
      reading: Reading | null;
      recent: Reading[];
      confirmedAtMs?: number | null;
      /** Cycle 435: the server's verdict from the same `latest` answer,
       *  present exactly when `confirmedAtMs` is. */
      verdict?: { stale: boolean; ageSeconds: number | null } | null;
      /** Cycle 435: the member the reseed was STARTED for (see
       *  `seed-alerts`). Dropped by the reducer on a mismatch. */
      memberId?: string | null;
    }
  | { type: "seed-dashboard"; cards: DashboardCard[] }
  // Cycle 101 — optimistic treatment insert / removal (voice logging).
  // `inject` upserts a client-authored row into the per-member cache
  // exactly like a `treatment` frame so the IOB ring updates instantly;
  // `forget` drops it (and records the id in deletedTreatmentIds so a
  // consumer's REST-seeded list drops it too) on reconcile.
  | { type: "inject-treatment"; memberId: string; treatment: Treatment }
  | { type: "forget-treatment"; memberId: string; id: string }
  // Cycle 112: drop the live-fire event fields on socket teardown so a
  // stale event can't fire after the connection is torn down (the
  // monotonic `seq` already protects the reopen path, but this makes
  // teardown explicit per the event-vs-cache contract).
  | { type: "clear-events" };

const initialState: State = {
  status: "idle",
  snapshotReceived: false,
  activeMemberId: null,
  lastReading: null,
  recentReadings: [],
  outstandingReminders: [],
  lastAlert: null,
  lastPrediction: null,
  lastAlertEvent: null,
  lastPredictionEvent: null,
  lastCheckinEvent: null,
  custodyHandBack: null,
  eventSeq: 0,
  unackCount: 0,
  urgentActive: false,
  voiceCallQuietUntilByMember: {},
  callQuietUntilByUser: {},
  byMember: {},
  dashboardCards: [],
  cgmAuthFailedByMember: {},
  serviceLockedByMember: {},
  serviceLockedAsOfByMember: {},
  activeBroadcast: null,
  lastFrameMeta: null,
  readingConfirmedAtMs: null,
  readingBoundMs: READING_CONFIRM_BOUND_MS,
  readingUnknownAfterMs: READING_CONFIRM_BOUND_MS + RESEED_TIMEOUT_MS,
  confirmationByMember: {},
  verdictByMember: {},
};

// Exported for the reducer's test only (`glucoseSocketSlot.test.ts`).
export const INITIAL_SOCKET_STATE: State = initialState;

const emptyMemberState: MemberStreamState = {
  lastReading: null,
  lastAlert: null,
  unackCount: 0,
  hasUnackedUrgent: false,
  recentTreatments: [],
  deletedTreatmentIds: new Set<string>(),
  alertRows: {},
  leadAnchors: {},
  pendingAnchors: {},
  serverRowTruth: {},
};

// Cycle 435: the truth map is bounded the same way the row map is — a
// long-lived kiosk session must not accrete every row it ever saw. Closed
// rows are the ones that stop mattering first, so they are the ones
// dropped, oldest statement first.
const ROW_TRUTH_CAP = 16;

/** Cycle 435: fold one server statement about a row into the member's
 *  truth map. `live` is the SERVER'S EPISODE RULE over the row's own
 *  columns — fired and not recovered; a snoozed, silenced or acknowledged
 *  row is live (`alert_routes.py::active_count`). ⚠ NOT `alertRowIsOpen`,
 *  which is the slot/audio rule and treats a silenced row as closed: the
 *  safety reviewer showed that a Stop on a plain low would then tear down
 *  a live episode the trigger effect re-opens at once, forever. The client
 *  computes nothing about the episode here; it only records what the
 *  server said. */
function rowIsLive(a: Alert): boolean {
  return a.fired_at != null && a.recovered_at == null;
}
function noteRowTruth(
  prev: Record<string, ServerRowTruth>,
  a: Alert,
  nowMs: number,
): Record<string, ServerRowTruth> {
  const next: Record<string, ServerRowTruth> = {
    ...prev,
    [a.id]: {
      live: rowIsLive(a),
      silenced: a.silenced_at != null,
      level: a.level,
      atMs: nowMs,
    },
  };
  const ids = Object.keys(next);
  if (ids.length > ROW_TRUTH_CAP) {
    // Only rows the server has said are no longer live are ever dropped;
    // a live row is never evicted — the map may exceed the cap rather than
    // forget a row the teardown predicate would then read as "not closed"
    // from silence.
    const dead = ids
      .filter((id) => !next[id]!.live)
      .sort((x, y) => next[x]!.atMs - next[y]!.atMs);
    for (const id of dead.slice(0, ids.length - ROW_TRUTH_CAP)) delete next[id];
  }
  return next;
}

/** Cycle 435: a SNAPSHOT's low-family open set is complete
 *  (`_open_alerts_payload` lists every open, UN-SILENCED low-family row
 *  for the member, and `latest_alert` is the newest row of any family). So
 *  a stored LOW-family row this device last saw as live and UN-SILENCED,
 *  which the snapshot does not name as live, has been recovered by the
 *  server while this device was not listening.
 *
 *  ⚠ Three things absence is NOT evidence of, each pinned: a HIGH-family
 *  row (the snapshot does not enumerate them — only a statement naming it
 *  counts); a row this device knows is SILENCED (the server filters
 *  silenced rows out of `open_alerts`, so its absence is expected — and a
 *  silenced row is a live episode); and anything at all when the caller
 *  decides the list is not complete (absent from the wire, or at the
 *  server's cap — the caller passes `null` then). */
function closeUnlistedLowRows(
  prev: Record<string, ServerRowTruth>,
  listedLiveIds: ReadonlySet<string> | null,
  nowMs: number,
): Record<string, ServerRowTruth> {
  if (listedLiveIds == null) return prev;
  let next = prev;
  for (const id of Object.keys(prev)) {
    const t = prev[id]!;
    if (t.live && !t.silenced && isLowFamily(t.level) && !listedLiveIds.has(id)) {
      if (next === prev) next = { ...prev };
      next[id] = { live: false, silenced: t.silenced, level: t.level, atMs: nowMs };
    }
  }
  return next;
}

// Cycle 410: the per-member row map is small by construction — a member
// has at most two open low-family rows on a descent — but it is bounded
// anyway so a long-lived kiosk session cannot accrete rows.
const ALERT_ROWS_CAP = 8;

/** Cycle 410 — fold one alert-family frame into the member's row map and
 *  lead anchors. Called from EVERY case that carries an `AlertOut` (the
 *  snapshot seed, `alert`, `alert_ack`, `alert_heartbeat`,
 *  `alert_recovered`, `alert_snoozed` / `alert_silenced`,
 *  `alert_treating`), so the audible-row selection sees the same truth the
 *  slot does. Closed rows other than the incoming one are dropped (the
 *  selector ignores them anyway); anchors follow their rows. */
function noteAlertRow(
  prev: MemberStreamState,
  a: Alert,
  nowMs: number = Date.now(),
): Pick<
  MemberStreamState,
  "alertRows" | "leadAnchors" | "pendingAnchors" | "serverRowTruth"
> {
  const merged: Record<string, Alert> = { ...prev.alertRows, [a.id]: a };
  const kept = Object.values(merged).filter(
    (r) =>
      r.id === a.id ||
      (r.fired_at != null && r.recovered_at == null && r.silenced_at == null),
  );
  kept.sort((x, y) => triggeredMs(y) - triggeredMs(x));
  const alertRows: Record<string, Alert> = {};
  for (const r of kept.slice(0, ALERT_ROWS_CAP)) alertRows[r.id] = r;
  const leadAnchors: Record<string, LeadAnchor> = {};
  for (const id of Object.keys(prev.leadAnchors)) {
    const held = prev.leadAnchors[id];
    if (id !== a.id && alertRows[id] != null && held != null) leadAnchors[id] = held;
  }
  // Cycle 410.1: a NEW window is accepted only while this device can still
  // be holding for it — an unknown row, or a known row whose pending grace
  // is live at receipt. A row this device already resolved loud on never
  // takes a window, whatever the server's clock says (the device rule
  // behind "never sounding → silent"; `leadAcceptedAtReceipt`).
  const anchor = foldLeadAnchorAtReceipt(
    prev.leadAnchors[a.id],
    prev.alertRows[a.id] != null,
    prev.pendingAnchors[a.id],
    a.audio_lead_until,
    nowMs,
  );
  if (anchor != null) leadAnchors[a.id] = anchor;
  // Cycle 410.1: the pending grace. Folded from EVERY alert-family frame
  // for the row, so any frame without the field resolves it (dropped).
  const pendingAnchors: Record<string, LeadAnchor> = {};
  for (const id of Object.keys(prev.pendingAnchors)) {
    const held = prev.pendingAnchors[id];
    if (id !== a.id && alertRows[id] != null && held != null) pendingAnchors[id] = held;
  }
  const pending = foldPendingAnchor(
    prev.pendingAnchors[a.id],
    a.audio_lead_pending,
    nowMs,
  );
  if (pending != null) pendingAnchors[a.id] = pending;
  return {
    alertRows,
    leadAnchors,
    pendingAnchors,
    // Cycle 435: every alert-family frame is a server statement about its row.
    serverRowTruth: noteRowTruth(prev.serverRowTruth, a, nowMs),
  };
}

function dashboardCardFromMember(m: MemberWithLatest): DashboardCard {
  return {
    member_id: m.id,
    display_name: m.display_name,
    relationship_label: m.relationship_label,
    is_self: m.is_self,
    cgm_provider: m.cgm_provider,
    latest_reading: m.latest_reading,
    stale: m.stale,
    age_seconds: m.age_seconds,
    current_alert_level: m.current_alert_level,
    // Cycle 468: the owner's lock, from the dashboard row.
    service_locked: m.service_locked === true,
  };
}

function bumpCardWithReading(
  cards: DashboardCard[],
  member_id: string,
  reading: Reading,
): DashboardCard[] {
  const idx = cards.findIndex((c) => c.member_id === member_id);
  if (idx < 0) return cards;
  const next = cards.slice();
  // ⚠ Cycle 435: the forced `stale: false` is GONE. A live frame proves the
  // reading arrived, not that it is fresh — a backfilled row can be half an
  // hour old on arrival (2026-09-14 16:28Z: six readings in one batch, the
  // newest 31 minutes old). The card carries the client's own age check
  // against the clock at receipt; the display re-judges it at paint time.
  const nowMs = Date.now();
  next[idx] = {
    ...next[idx]!,
    latest_reading: reading,
    stale: isStale(reading.timestamp, nowMs, reading.provider),
    age_seconds: Math.round(ageSeconds(reading.timestamp, nowMs)),
  };
  return next;
}

function bumpCardWithAlert(
  cards: DashboardCard[],
  member_id: string,
  level: AlertLevel,
): DashboardCard[] {
  const idx = cards.findIndex((c) => c.member_id === member_id);
  if (idx < 0) return cards;
  const next = cards.slice();
  next[idx] = { ...next[idx]!, current_alert_level: level };
  return next;
}

function applyMemberToCards(
  cards: DashboardCard[],
  member: Member,
): DashboardCard[] {
  // Archive removes the card; otherwise upsert by id while preserving
  // any live reading/age fields already on the row.
  if (member.archived_at != null) {
    return cards.filter((c) => c.member_id !== member.id);
  }
  const idx = cards.findIndex((c) => c.member_id === member.id);
  if (idx < 0) {
    return [
      ...cards,
      {
        member_id: member.id,
        display_name: member.display_name,
        relationship_label: member.relationship_label,
        is_self: member.is_self,
        cgm_provider: member.cgm_provider,
        latest_reading: null,
        stale: true,
        age_seconds: null,
        current_alert_level: null,
      },
    ];
  }
  const next = cards.slice();
  next[idx] = {
    ...next[idx]!,
    display_name: member.display_name,
    relationship_label: member.relationship_label,
    is_self: member.is_self,
    cgm_provider: member.cgm_provider,
  };
  return next;
}

// ─── Cycle 408: ONE SLOT, TWO OPEN ROWS — THE LIVE ONE WINS ───────────────
//
// A real hypo descends through `low` before `urgent_low`, so the server
// keeps TWO open low-family rows for one episode (cycle 231 recovers them
// together). This state holds ONE `lastAlert` per member, and every alarm
// frame used to overwrite it unconditionally — so whichever row's frame
// landed last was "the episode". On 2026-08-30 the phone's slot flipped to
// the member's `low` row while his `urgent_low` was sounding: `useAlarmEpisode`
// bound `alertId` to it, six "I'm treating this" taps POSTed against the
// `low` row and 409'd `not_urgent_low` under an URGENT LOW banner, and the
// treating screen appeared and vanished with each tap.
//
// THE RULE: between two OPEN rows of the same family, the NEWER row (by
// `triggered_at` — the reading's own clock) is the live one and holds the
// slot; on an equal timestamp the more severe wins. Same id ⇒ always the
// refresh. Either row closed ⇒ newest frame wins, exactly as before — so
// recovery (both rows, together) and every terminal state still converge.
//
// Why NEWEST and not MOST SEVERE (the first draft; the safety review's
// findings 1 and 2): on the way DOWN the urgent row is the newer one, so
// both rules agree. On the way back UP the engine fires a NEW `low` row
// (the urgent row is still open) and every server sweep follows that
// newest row — a severity rule would keep the old urgent row bound and
// send the household's snooze to a row nobody is hearing. And a device
// that missed a recovery frame holds a STALE open urgent in its cache; the
// reconnect snapshot brings the new episode's `low`, which is newer, so it
// wins — a severity rule would have wedged the slot on the recovered row.
const FAMILY_SEVERITY: Record<AlertLevel, number> = {
  low: 1,
  urgent_low: 2,
  high: 1,
  urgent_high: 2,
};
function isLowFamily(level: AlertLevel): boolean {
  return level === "low" || level === "urgent_low";
}
// "Open" here matches the server's sibling rule exactly: fired, not
// recovered, not silenced (Stop is terminal for the row — cycle 66).
function alertRowIsOpen(a: Alert): boolean {
  return a.fired_at != null && a.recovered_at == null && a.silenced_at == null;
}
function triggeredMs(a: Alert): number {
  const ms = new Date(a.triggered_at).getTime();
  return Number.isFinite(ms) ? ms : 0;
}
/** Cycle 435: a reading's own sample time in epoch ms, `null` when absent
 *  or unparseable — the key a confirmation and a verdict are stamped with. */
function readingTsMs(r: Reading | null | undefined): number | null {
  if (r == null) return null;
  const ms = new Date(r.timestamp).getTime();
  return Number.isFinite(ms) ? ms : null;
}
export function preferSoundingRow(prev: Alert | null, next: Alert): Alert {
  if (prev == null || prev.id === next.id) return next;
  if (!alertRowIsOpen(prev) || !alertRowIsOpen(next)) return next;
  if (isLowFamily(prev.level) !== isLowFamily(next.level)) return next;
  const dt = triggeredMs(next) - triggeredMs(prev);
  if (dt > 0) return next;
  if (dt < 0) return prev;
  return FAMILY_SEVERITY[next.level] >= FAMILY_SEVERITY[prev.level]
    ? next
    : prev;
}

/** Cycle 429 — THE SLOT FOLLOWS SERVER TRUTH. Given the server's own rows
 *  for a member (a snapshot's `open_alerts` + `latest_alert`, or a REST
 *  reseed's list), choose the slot from THOSE rows alone — never from the
 *  row this device remembers, which on 2026-09-10 was the row a dead
 *  socket had left behind hours earlier. An OPEN low-family row beats an
 *  open high-family row (the urgent takeover is the surface that must
 *  bind); among open rows of one family `preferSoundingRow` decides
 *  (newest, then more severe); with no open row the newest row (`latest`)
 *  converges the episode to whatever the server says happened — a
 *  recovered row ends it. Pure; pinned in `socketCatchUp.test.ts`. */
export function slotFromServerRows(
  openRows: readonly Alert[] | null | undefined,
  latest: Alert | null | undefined,
): Alert | null {
  let low: Alert | null = null;
  let high: Alert | null = null;
  const candidates: Alert[] = [];
  for (const r of openRows ?? []) if (r) candidates.push(r);
  if (latest) candidates.push(latest);
  for (const r of candidates) {
    if (!alertRowIsOpen(r)) continue;
    if (isLowFamily(r.level)) low = low ? preferSoundingRow(low, r) : r;
    else high = high ? preferSoundingRow(high, r) : r;
  }
  return low ?? high ?? latest ?? null;
}

/** The meta a lone LIVE frame carries (the legacy single-frame action). */
function liveMeta(nowMs: number): DeliveryMeta {
  return { receivedAtMs: nowMs, catchUp: false, size: 1 };
}

// Exported for the reducer's own test (`glucoseSocketSlot.test.ts`); the
// hook is the only production caller.
export function reduce(s: State, a: Action): State {
  switch (a.type) {
    case "status":
      return { ...s, status: a.status };
    case "ack-urgent":
      return { ...s, urgentActive: false };
    case "set-active":
      // ⚠ Cycle 474 (B8 amplifier): the SAME member again is not a switch.
      // The member effect re-dispatches this on every token ROTATION (its
      // deps include the token), and blanking the held reading there wiped
      // the hero to No Data on a refresh — the very refresh that heals an
      // expired session after sleep. Only the live-fire events are dropped
      // (cycle 112: nothing captured before may fire a sound after it).
      if (a.memberId != null && a.memberId === s.activeMemberId) {
        return { ...s, lastAlertEvent: null, lastPredictionEvent: null, lastCheckinEvent: null };
      }
      return {
        ...s,
        activeMemberId: a.memberId,
        // Active-scoped fields drop on switch; the seed actions
        // (REST seed + the next snapshot/glucose frame for this
        // member) refill them.
        lastReading: null,
        recentReadings: [],
        lastAlert: null,
        lastPrediction: null,
        // Cycle 112: clear the live-fire events too so a stale event
        // from the previous member can't fire a sound after the switch.
        lastAlertEvent: null,
        lastPredictionEvent: null,
        // Cycle 407: a question posed to the member must never be spoken over
        // Ben's screen, and the member's hand-back never names Ben.
        lastCheckinEvent: null,
        custodyHandBack: null,
      };
    case "clear-events":
      return {
        ...s,
        lastAlertEvent: null,
        lastPredictionEvent: null,
        lastCheckinEvent: null,
      };
    case "reset-account":
      // Cycle 435 (security review): a different ACCOUNT signed in inside
      // the mounted shell. Everything the previous account's socket seeded
      // — its members' readings, cards, confirmations, verdicts and rows —
      // is gone before the new account's snapshot lands, so nothing of the
      // old household can render as current in the gap. `status` is kept:
      // the socket effect that dispatched this is about to reconnect.
      // ⚠ `eventSeq` is carried, not rewound (regression-guard): the
      // one-shot cue consumers compare by EQUALITY against the last seq
      // they handled, so a rewind to 0 could make the new account's first
      // live alert (seq 1) equal the old account's last handled seq and be
      // swallowed — a missed alarm sound. Monotonic across the reset.
      // ⚠ `activeBroadcast` is DROPPED (security re-run): an operator
      // broadcast can be addressed to one account, and the reviewers split
      // on it — the safety pass judged carrying it harmless, the security
      // pass judged it another account's text on this screen. Dropping is
      // the quieter error: it is operator text, never an alarm surface,
      // control or sound, it already carries its own dismiss, and carrying
      // it cannot be undone once it has been read.
      // ⚠ It is NOT re-seeded, and an earlier draft of this comment
      // claimed it was (caught by the safety re-run): a non-cleared
      // `admin_broadcast` frame is emitted only by POST /api/admin/broadcast,
      // the WS connect snapshot carries no broadcast, and the only
      // rehydration route is admin-only. So a live banner — an urgent one
      // included — is gone on this device until the operator broadcasts
      // again. Accepted: an in-place sign-in as a different account is a
      // rare, deliberate act and the banner is already dismissible.
      return {
        ...initialState,
        status: s.status,
        eventSeq: s.eventSeq,
      };
    case "seed": {
      // ⚠ Cycle 435 (security review): a REST answer is attributed to the
      // member the reseed was STARTED for, never to whoever is active at
      // reduce time. A wake reseed for member A that lands after a switch
      // to B used to write A's reading — and now A's confirmation — under
      // B. Dropped instead; B's own reseed is already on its way.
      if (a.memberId != null && a.memberId !== s.activeMemberId) return s;
      // Cycle 433: a reseed that ANSWERED is a confirmation, even when the
      // answer is "the newest reading is the one you already have" — the
      // server having nothing newer is exactly the proof the display
      // needs. A reseed whose reading call failed passes null and the
      // clock stands still.
      //
      // Cycle 435: the confirmation is recorded FOR THE ACTIVE MEMBER (the
      // reseed is scoped to it), together with the server's verdict from the
      // same answer. An answer with NO reading confirms too — as "empty".
      let confirmationByMember = s.confirmationByMember;
      let verdictByMember = s.verdictByMember;
      const memberId = s.activeMemberId;
      // ⚠ A confirmation is written ONLY for a seed that carries the member
      // it was started for and still matches (security review, LOW): an
      // untagged answer — a reseed begun before the client had resolved a
      // member — still lands its READING as before, but proves nothing for
      // whoever happens to be active at reduce time.
      if (a.confirmedAtMs != null && memberId != null && a.memberId === memberId) {
        const readingTimestampMs = readingTsMs(a.reading);
        confirmationByMember = {
          ...s.confirmationByMember,
          [memberId]: {
            receivedAtMs: a.confirmedAtMs,
            readingTimestampMs,
            source: "rest_latest",
          },
        };
        verdictByMember = { ...s.verdictByMember };
        if (a.verdict) {
          verdictByMember[memberId] = {
            stale: a.verdict.stale,
            ageSeconds: a.verdict.ageSeconds,
            receivedAtMs: a.confirmedAtMs,
            readingTimestampMs,
          };
        } else {
          delete verdictByMember[memberId];
        }
      }
      return {
        ...s,
        lastReading: a.reading,
        recentReadings: a.recent,
        readingConfirmedAtMs: a.confirmedAtMs ?? s.readingConfirmedAtMs,
        confirmationByMember,
        verdictByMember,
      };
    }
    case "seed-dashboard": {
      // Replace dashboardCards with the REST seed but preserve any
      // per-member readings already received via WS (frames may
      // arrive before the REST round-trip completes).
      // Cycle 468: a LOCKED member's row never takes a live reading
      // either — the server withheld it on purpose. The row SEEDS the
      // per-member lock map only where no entry exists yet: a WS
      // `service_state` frame is the live source, and a REST reseed that
      // raced a lock or an unlock must never overwrite it (security round 2).
      const serviceLockedByMember = { ...s.serviceLockedByMember };
      const merged = a.cards.map((card) => {
        if (card.service_locked != null && serviceLockedByMember[card.member_id] == null) {
          serviceLockedByMember[card.member_id] = card.service_locked === true;
        }
        const live = s.byMember[card.member_id];
        if (live?.lastReading && card.latest_reading == null && card.service_locked !== true) {
          return { ...card, latest_reading: live.lastReading };
        }
        return card;
      });
      return { ...s, dashboardCards: merged, serviceLockedByMember };
    }
    case "inject-treatment": {
      // Cycle 101: optimistic upsert, identical shape to the `treatment`
      // frame case, so consumers that merge `recentTreatments` (NowScreen
      // → IOB ring) pick it up immediately.
      const prev = s.byMember[a.memberId] ?? emptyMemberState;
      const without = prev.recentTreatments.filter((t) => t.id !== a.treatment.id);
      const next = [a.treatment, ...without].slice(0, TREATMENTS_CAP);
      return {
        ...s,
        byMember: {
          ...s.byMember,
          [a.memberId]: { ...prev, recentTreatments: next },
        },
      };
    }
    case "forget-treatment": {
      // Cycle 101: drop the optimistic row on reconcile. Record the id in
      // deletedTreatmentIds (mirrors `treatment_deleted`) so a consumer's
      // REST-seeded list drops it too — the client id is unique, so
      // blacklisting it forever is harmless and never hides a real row.
      const prev = s.byMember[a.memberId] ?? emptyMemberState;
      const nextTreatments = prev.recentTreatments.filter((t) => t.id !== a.id);
      const nextDeleted = new Set(prev.deletedTreatmentIds);
      nextDeleted.add(a.id);
      return {
        ...s,
        byMember: {
          ...s.byMember,
          [a.memberId]: {
            ...prev,
            recentTreatments: nextTreatments,
            deletedTreatmentIds: nextDeleted,
          },
        },
      };
    }
    case "frames": {
      // Cycle 429: apply in order; the batch's meta rides on the state so
      // the cues know whether the last delivery was live or a backlog.
      // A batch that carries a snapshot is a SEED whatever its size — for
      // every frame in it, not only the stamp it leaves behind.
      const seeded = a.frames.some((f) => f.type === "snapshot");
      const meta: DeliveryMeta = seeded ? { ...a.meta, catchUp: true } : a.meta;
      let st = s;
      for (const f of a.frames) {
        st = reduce(st, { type: "frame", frame: f, meta });
      }
      // Cycle 433: a batch carrying a READING (a snapshot's member rows or a
      // `glucose` frame, whichever member) is the server saying what the
      // newest reading is — the proof of currency. A batch of pongs,
      // acks or heartbeats is not, which is the whole point: the
      // 2026-09-13 socket looked healthy and delivered no readings.
      const confirmed = a.frames.some(
        (f) => f.type === "snapshot" || f.type === "glucose",
      );
      return {
        ...st,
        lastFrameMeta: meta,
        readingConfirmedAtMs: confirmed
          ? meta.receivedAtMs
          : st.readingConfirmedAtMs,
      };
    }
    case "seed-alerts": {
      // Cycle 429: the wake reseed. The list is server-scoped to the ACTIVE
      // member; with no active member there is nothing to bind.
      const memberId = s.activeMemberId;
      if (!memberId) return s;
      // ⚠ An EMPTY list is not "nothing is open" — it is "nothing in the
      // listed window". The snapshot is the authority for emptiness; a
      // reseed that lists nothing keeps the slot it has rather than
      // nulling a live binding (regression-guard, note 1).
      const prev = s.byMember[memberId] ?? emptyMemberState;
      // On every early return the ACTIVE member's cached slot is mirrored
      // up: `set-active` nulls the top-level slot while the per-member
      // cache keeps the open row, and `prev` is by construction the active
      // member's state (safety review, post-review note 3).
      const keep = (): State => ({ ...s, lastAlert: prev.lastAlert });
      // ⚠ Cycle 435 (security review): the rows belong to the member the
      // reseed was STARTED for. A list that lands after a member switch is
      // dropped — and every row self-verifies by its own `member_id`, so a
      // list that names another member's rows binds nothing here. Dropped
      // through `keep()`, like every other early return, so the active
      // member's cached slot is still mirrored up.
      if (a.memberId != null && a.memberId !== memberId) return keep();
      if (a.alerts.some((r) => r.member_id !== memberId)) return keep();
      if (a.alerts.length === 0) return keep();
      // ⚠ NEVER DOWNGRADE ON A TIME-BOUNDED LIST. If the slot already holds
      // an OPEN row the list does not mention — a row older than the
      // listed window, the three-hour urgent low of 2026-09-08 — the list
      // says nothing about it, and a newer recovered row in the window
      // must not end an episode the snapshot (time-unbounded) still holds
      // open (safety review, finding 2). The list may REBIND — it names the
      // held row, or it carries a newer OPEN row (an upgrade is always
      // allowed) — but it may not conclude "nothing is open" from silence.
      const held = prev.lastAlert;
      const heldNamed = held != null && a.alerts.some((r) => r.id === held.id);
      // The upgrade must be one that would WIN the slot: a low-family row
      // always may; a high-family row only over a high-family holder — an
      // unlisted open low is never displaced by a listed newer high.
      const newerOpenListed =
        held != null &&
        a.alerts.some(
          (r) =>
            alertRowIsOpen(r) &&
            triggeredMs(r) > triggeredMs(held) &&
            (isLowFamily(r.level) || !isLowFamily(held.level)),
        );
      if (
        held != null &&
        alertRowIsOpen(held) &&
        !heldNamed &&
        !newerOpenListed
      ) {
        return keep();
      }
      const open = a.alerts.filter(alertRowIsOpen);
      const newest = a.alerts.reduce<Alert | null>(
        (acc, r) =>
          acc == null || triggeredMs(r) > triggeredMs(acc) ? r : acc,
        null,
      );
      let seeded: MemberStreamState = prev;
      for (const row of open) {
        seeded = { ...seeded, ...noteAlertRow(seeded, row) };
      }
      // Cycle 435: EVERY listed row is a server statement about that row —
      // the closed ones included. A row this device still holds an episode
      // on, listed here with `recovered_at` set, is the "stamp that can
      // never arrive" of 2026-09-14 20:27Z, arriving by the other door.
      {
        const nowMs = Date.now();
        let truth = seeded.serverRowTruth;
        for (const row of a.alerts) truth = noteRowTruth(truth, row, nowMs);
        seeded = { ...seeded, serverRowTruth: truth };
      }
      const slot = slotFromServerRows(open, newest);
      return {
        ...s,
        byMember: { ...s.byMember, [memberId]: { ...seeded, lastAlert: slot } },
        lastAlert: slot,
        lastFrameMeta: {
          receivedAtMs: Date.now(),
          catchUp: true,
          size: a.alerts.length,
        },
      };
    }
    case "frame": {
      const f = a.frame;
      // Cycle 429: a backlog, a thaw or a (re)connect seed is applied as
      // STATE — the row caches, the slot, the deadlines and the flags
      // converge exactly as before, but no live-fire event
      // (`lastAlertEvent`, `lastPredictionEvent`, `lastCheckinEvent`, a
      // hand-back line) is minted, so nothing downstream can replay it.
      const meta = a.meta ?? liveMeta(Date.now());
      const catchUp = meta.catchUp;
      if (a.meta == null) s = { ...s, lastFrameMeta: meta };
      switch (f.type) {
        case "snapshot": {
          const activeRow = s.activeMemberId
            ? f.members.find((m) => m.member_id === s.activeMemberId)
            : null;
          const fallback =
            f.members.find((m) => m.reading) ?? f.members[0] ?? null;
          const row = activeRow ?? fallback;
          const reading = row?.reading ?? null;
          // Cycle 61: connect-time snapshot carries each member's
          // latest alert so a device joining mid-episode (or waking
          // via cycle-33 wake-refresh) converges on the current
          // snoozed / silenced state without waiting for the next
          // Address / Silence press. Seed both per-member lastAlert
          // and (when the member is active) top-level lastAlert.
          const byMember = { ...s.byMember };
          // Cycle 88: seed per-member CGM auth-failed state so a device
          // connecting mid-failure shows the modal immediately.
          const cgmAuthFailedByMember = { ...s.cgmAuthFailedByMember };
          // Cycle 435: a snapshot row is the server's statement about THAT
          // member's newest reading — a confirmation and a verdict, per
          // member, stamped with the reading it judged.
          const confirmationByMember = { ...s.confirmationByMember };
          const verdictByMember = { ...s.verdictByMember };
          for (const m of f.members) {
            const prev = byMember[m.member_id] ?? emptyMemberState;
            const snapTs = readingTsMs(m.reading);
            confirmationByMember[m.member_id] = {
              receivedAtMs: meta.receivedAtMs,
              readingTimestampMs: snapTs,
              source: "snapshot",
            };
            verdictByMember[m.member_id] = {
              stale: m.stale,
              ageSeconds: m.age_seconds ?? null,
              receivedAtMs: meta.receivedAtMs,
              readingTimestampMs: snapTs,
            };
            // Cycle 429: the slot is chosen from the SERVER's rows alone —
            // never folded against the row this device remembers, which is
            // exactly the row a dead socket left behind.
            const seededAlert = slotFromServerRows(
              m.open_alerts,
              m.latest_alert,
            );
            // Cycle 410: seed every OPEN low-family row the server sent —
            // the `low` sibling included — so the descent rule survives a
            // reconnect / wake / reload mid-lead-window (a snapshot that
            // seeded only `latest_alert` turned a descent into the
            // cold-crossing silence on that device). Slot untouched.
            let seeded: MemberStreamState = prev;
            for (const row of m.open_alerts ?? []) {
              seeded = { ...seeded, ...noteAlertRow(seeded, row, meta.receivedAtMs) };
            }
            if (m.latest_alert) {
              seeded = { ...seeded, ...noteAlertRow(seeded, m.latest_alert, meta.receivedAtMs) };
            }
            // ⚠ Cycle 435: the snapshot's low-family open set is COMPLETE
            // when it is on the wire and under the server's cap, so a
            // LOW-family row this device last saw as live and un-silenced
            // that the snapshot does not name as live was recovered by the
            // server while this device was not listening. Marked closed
            // here — `useAlarmEpisode` reads it to tear down a dead episode.
            // An ABSENT `open_alerts` (an older server) or a list AT the cap
            // (`limit(4)`, possibly truncated) is not complete, and then
            // absence says nothing (`null`). Nothing else changes: the slot
            // is still `slotFromServerRows`, the audio rows are untouched.
            let listedLive: Set<string> | null = null;
            if (m.open_alerts != null && m.open_alerts.length < SNAPSHOT_OPEN_ALERTS_LIMIT) {
              listedLive = new Set<string>();
              for (const row of m.open_alerts) {
                if (rowIsLive(row)) listedLive.add(row.id);
              }
              if (m.latest_alert && rowIsLive(m.latest_alert)) {
                listedLive.add(m.latest_alert.id);
              }
            }
            byMember[m.member_id] = {
              ...seeded,
              serverRowTruth: closeUnlistedLowRows(
                seeded.serverRowTruth,
                listedLive,
                meta.receivedAtMs,
              ),
              lastReading: m.reading ?? prev.lastReading ?? null,
              lastAlert: seededAlert,
            };
            cgmAuthFailedByMember[m.member_id] = m.cgm_auth_failed ?? false;
          }
          // Pick the active member's alert (if any) for top-level
          // mirroring. Fallback row is reading-driven; we don't
          // promote an arbitrary member's alert to top-level just
          // because they have one.
          //
          // Cycle 231 — audio provenance: this snapshot seeds episode/
          // banner STATE only (`byMember[*].lastAlert`, top-level
          // `lastAlert`). It deliberately does NOT touch `lastAlertEvent`
          // / `eventSeq` (the cycle-112 live-fire fields the one-shot
          // alarm sounds key off), so a per-member `latest_alert` seed is
          // never replayed as a freshly-fired alert event. The continuous
          // loop is separately gated to the active member + a fresh
          // reading in `useAlarmSounds` (so a fallback-reading seed for a
          // non-active member can't momentarily arm it). State converges
          // silently; audio fires only on genuine live transitions.
          const topAlert = activeRow
            ? slotFromServerRows(activeRow.open_alerts, activeRow.latest_alert)
            : null;
          return {
            ...s,
            snapshotReceived: true,
            byMember,
            cgmAuthFailedByMember,
            confirmationByMember,
            verdictByMember,
            lastReading: reading,
            recentReadings: reading ? [reading] : [],
            lastAlert: topAlert,
            // Cycle 429: a snapshot is a seed, never an event source.
            lastFrameMeta: { ...meta, catchUp: true },
            // Cycle 362: WHOLESALE replace from server truth — this is the
            // reconciliation path. Replacing (not merging) is the point: it
            // both surfaces an occurrence this device never saw and drops one
            // that was acknowledged elsewhere while this device was offline,
            // which is what let a reminder linger or go missing before.
            // A pre-362 server omits the field → treated as "nothing
            // outstanding", the correct non-alarming default.
            outstandingReminders: f.outstanding_reminders ?? [],
          };
        }
        case "service_state": {
          // Cycle 468: the member's monitoring is on or suspended by the
          // subscription lock. Per member; the active member's value is read
          // at the hook's return. A frame older than the one already applied
          // for the member (its `as_of`) is dropped: the sweep's lock frame
          // and a purchase's unlock frame can be delivered out of order.
          // Compared at microsecond precision (astra round 2): two reads
          // inside one millisecond must keep their order.
          const asOf = asOfKey(f.as_of);
          const have = s.serviceLockedAsOfByMember[f.member_id];
          if (Number.isFinite(asOf) && have != null && asOf < have) return s;
          return {
            ...s,
            serviceLockedByMember: {
              ...s.serviceLockedByMember,
              [f.member_id]: f.state === "locked",
            },
            serviceLockedAsOfByMember: Number.isFinite(asOf)
              ? { ...s.serviceLockedAsOfByMember, [f.member_id]: asOf }
              : s.serviceLockedAsOfByMember,
          };
        }
        case "cgm_auth": {
          // Cycle 88: member-scoped credential-failure state. Updates the
          // per-member map for any member; the active-member value (read
          // at the hook's return) drives the modal. An auth_failed:false
          // frame clears it everywhere.
          return {
            ...s,
            cgmAuthFailedByMember: {
              ...s.cgmAuthFailedByMember,
              [f.member_id]: f.auth_failed,
            },
          };
        }
        case "glucose": {
          const r = f.reading;
          // Always update the per-member map and the dashboard card.
          const prev = s.byMember[f.member_id] ?? emptyMemberState;
          const byMember = {
            ...s.byMember,
            [f.member_id]: { ...prev, lastReading: r },
          };
          const dashboardCards = bumpCardWithReading(
            s.dashboardCards,
            f.member_id,
            r,
          );
          // ⚠ Cycle 435: a `glucose` frame confirms THIS member and no other,
          // and only when it is the newest applied reading for the member —
          // a backfilled older row proves the link, not currency. It carries
          // no verdict, so the member's server verdict is cleared: the
          // server said nothing about this delivery and only the client's
          // own age check applies to it.
          const frameTs = readingTsMs(r);
          const heldTs = readingTsMs(
            f.member_id === s.activeMemberId ? s.lastReading : prev.lastReading,
          );
          const newest = frameTs != null && (heldTs == null || frameTs >= heldTs);
          let confirmationByMember = s.confirmationByMember;
          let verdictByMember = s.verdictByMember;
          if (newest) {
            confirmationByMember = {
              ...s.confirmationByMember,
              [f.member_id]: {
                receivedAtMs: meta.receivedAtMs,
                readingTimestampMs: frameTs,
                source: "glucose_frame",
              },
            };
            if (f.member_id in s.verdictByMember) {
              verdictByMember = { ...s.verdictByMember };
              delete verdictByMember[f.member_id];
            }
          }
          // Mirror to top-level (active-member-scoped) state only when
          // this frame is for the active member.
          if (s.activeMemberId && f.member_id !== s.activeMemberId) {
            return { ...s, byMember, dashboardCards, confirmationByMember, verdictByMember };
          }
          const dedup = s.recentReadings.filter((x) => x.id !== r.id);
          const next = [...dedup, r].slice(-READINGS_CAP);
          return {
            ...s,
            byMember,
            dashboardCards,
            confirmationByMember,
            verdictByMember,
            lastReading: r,
            recentReadings: next,
          };
        }
        case "alert": {
          const a2 = f.alert;
          const isUrgent =
            a2.level === "urgent_low" || a2.level === "urgent_high";
          const isNewUnack =
            a2.acknowledged_at == null && a2.fired_at != null;
          // Update per-member map.
          const prev = s.byMember[f.member_id] ?? emptyMemberState;
          const byMember = {
            ...s.byMember,
            [f.member_id]: {
              ...prev,
              ...noteAlertRow(prev, a2),
              lastAlert: preferSoundingRow(prev.lastAlert, a2),
              unackCount: isNewUnack ? prev.unackCount + 1 : prev.unackCount,
              hasUnackedUrgent:
                isUrgent && isNewUnack ? true : prev.hasUnackedUrgent,
            },
          };
          const dashboardCards = bumpCardWithAlert(
            s.dashboardCards,
            f.member_id,
            a2.level,
          );
          // Top-level mirroring is active-member-only for `lastAlert`
          // and the urgent-overlay flag — that gates audio.
          // `unackCount` is household-wide (kiosk badge counts every
          // member's pending acks).
          const isActiveMember =
            !s.activeMemberId || f.member_id === s.activeMemberId;
          // Cycle 112: a live `alert` frame for the active member emits a
          // fresh event (new monotonic `seq`) that the one-shot alarm
          // sounds key off. This is the ONLY place `lastAlertEvent` is
          // set — snapshot seeds, reconnect/wake re-seeds, and the
          // state-refresh frames (heartbeat/snooze/silence/recovered/ack)
          // all leave it untouched, so a deploy-reload that re-seeds a
          // stale unacked alert can't replay the sound. The cycle-66
          // mild→urgent re-arm rebroadcasts the same id at a new level —
          // a new `seq`, so it correctly re-fires.
          // Cycle 429: a backlog mints NO event — state only.
          const mintEvent = isActiveMember && !catchUp;
          const nextSeq = mintEvent ? s.eventSeq + 1 : s.eventSeq;
          return {
            ...s,
            byMember,
            dashboardCards,
            lastAlert: isActiveMember
              ? preferSoundingRow(s.lastAlert, a2)
              : s.lastAlert,
            // The EVENT (the one-shot sound) still fires for the row that
            // actually crossed — only the cached episode row is guarded.
            lastAlertEvent: mintEvent
              ? { alert: a2, seq: nextSeq }
              : s.lastAlertEvent,
            eventSeq: nextSeq,
            unackCount: isNewUnack ? s.unackCount + 1 : s.unackCount,
            urgentActive:
              isActiveMember && isUrgent && isNewUnack
                ? true
                : s.urgentActive,
          };
        }
        case "prediction": {
          // Predictions update byMember unconditionally; mirror to
          // top-level only for the active member so the prediction
          // pill follows whoever is on screen.
          if (s.activeMemberId && f.member_id !== s.activeMemberId) {
            return s;
          }
          // Cycle 112: a live `prediction` frame for the active member
          // emits a fresh event (new `seq`) — the only place
          // `lastPredictionEvent` is set, so the prediction sound never
          // replays from a seed. (Predictions aren't snapshot-seeded
          // today; keying the sound off an explicit event makes that
          // true by construction rather than by accident.)
          // Cycle 429: a backlog mints no event.
          if (catchUp) return { ...s, lastPrediction: f.prediction };
          const nextSeq = s.eventSeq + 1;
          return {
            ...s,
            lastPrediction: f.prediction,
            lastPredictionEvent: { prediction: f.prediction, seq: nextSeq },
            eventSeq: nextSeq,
          };
        }
        case "treatment": {
          // Upsert by id into the per-member treatment cache. Same
          // client that initiated the create will receive its own
          // broadcast — the upsert keeps that idempotent.
          const prev = s.byMember[f.member_id] ?? emptyMemberState;
          const without = prev.recentTreatments.filter(
            (t) => t.id !== f.treatment.id,
          );
          const next = [f.treatment, ...without].slice(0, TREATMENTS_CAP);
          return {
            ...s,
            byMember: {
              ...s.byMember,
              [f.member_id]: { ...prev, recentTreatments: next },
            },
          };
        }
        case "treatment_deleted": {
          const prev = s.byMember[f.member_id] ?? emptyMemberState;
          const nextTreatments = prev.recentTreatments.filter(
            (t) => t.id !== f.treatment_id,
          );
          // Always record the deletion id so a REST-seeded list on a
          // consumer screen can drop it too — even if the row was
          // never in the per-member cache.
          const nextDeleted = new Set(prev.deletedTreatmentIds);
          nextDeleted.add(f.treatment_id);
          return {
            ...s,
            byMember: {
              ...s.byMember,
              [f.member_id]: {
                ...prev,
                recentTreatments: nextTreatments,
                deletedTreatmentIds: nextDeleted,
              },
            },
          };
        }
        case "alert_ack": {
          const a2 = f.alert;
          const prev = s.byMember[f.member_id] ?? emptyMemberState;
          // Was this alert counted in the unackCount? It was iff the
          // current per-member lastAlert tracks this id and is still
          // unacked. Best-effort — the count seeds from REST on
          // member-switch anyway.
          const wasCounted =
            prev.lastAlert?.id === a2.id &&
            prev.lastAlert?.acknowledged_at == null;
          const lastAlertNext =
            prev.lastAlert?.id === a2.id ? a2 : prev.lastAlert;
          const byMember = {
            ...s.byMember,
            [f.member_id]: {
              ...prev,
              ...noteAlertRow(prev, a2),
              lastAlert: lastAlertNext,
              unackCount: Math.max(
                0,
                prev.unackCount - (wasCounted ? 1 : 0),
              ),
              hasUnackedUrgent: wasCounted ? false : prev.hasUnackedUrgent,
            },
          };
          const topMatches = s.lastAlert?.id === a2.id;
          return {
            ...s,
            byMember,
            lastAlert: topMatches ? a2 : s.lastAlert,
            unackCount: Math.max(0, s.unackCount - (wasCounted ? 1 : 0)),
            urgentActive: topMatches ? false : s.urgentActive,
          };
        }
        case "alert_heartbeat": {
          // Cycle 63: sustained-alarm heartbeat. Always upsert the
          // refreshed AlertOut into per-member + top-level lastAlert
          // (same posture as cycle-61 alert_snoozed/alert_silenced
          // upsert — never keep a stale cached row on id mismatch).
          // A heartbeat is NOT a new alert and NOT an
          // acknowledgement, so unackCount / hasUnackedUrgent /
          // urgentActive are intentionally untouched.
          const a2 = f.alert;
          const prev = s.byMember[f.member_id] ?? emptyMemberState;
          const byMember = {
            ...s.byMember,
            [f.member_id]: {
              ...prev,
              ...noteAlertRow(prev, a2),
              lastAlert: preferSoundingRow(prev.lastAlert, a2),
            },
          };
          const isActiveMember =
            !s.activeMemberId || f.member_id === s.activeMemberId;
          return {
            ...s,
            byMember,
            lastAlert: isActiveMember
              ? preferSoundingRow(s.lastAlert, a2)
              : s.lastAlert,
          };
        }
        case "alert_recovered": {
          // Cycle 82: server-authored recovery. The engine stamped
          // `recovered_at` on the open row and broadcast the refreshed
          // AlertOut. Always upsert it (same posture as
          // alert_heartbeat / alert_silenced — never keep a stale
          // cached row) so `useAlarmEpisode` sees `recovered_at` and
          // resets the episode on every device together. Recovery is
          // NOT an acknowledgement, so `unackCount` / `hasUnackedUrgent`
          // are untouched; but the episode is over, so the top-level
          // `urgentActive` flag drops for the active member (audio is
          // already gated off by the derived `audioOn`).
          const a2 = f.alert;
          const prev = s.byMember[f.member_id] ?? emptyMemberState;
          const byMember = {
            ...s.byMember,
            [f.member_id]: {
              ...prev,
              ...noteAlertRow(prev, a2),
              lastAlert: preferSoundingRow(prev.lastAlert, a2),
            },
          };
          const isActiveMember =
            !s.activeMemberId || f.member_id === s.activeMemberId;
          return {
            ...s,
            byMember,
            lastAlert: isActiveMember
              ? preferSoundingRow(s.lastAlert, a2)
              : s.lastAlert,
            urgentActive: isActiveMember ? false : s.urgentActive,
          };
        }
        case "alert_snoozed":
        case "alert_silenced": {
          // Cycle 38 + cycle 61: cross-device episode state. The
          // incoming AlertOut is the server's authoritative snapshot
          // of the alert at the moment Address / Silence was pressed
          // (carrying the new snoozed_until / silenced_at columns).
          //
          // Cycle 61 fix: always upsert this row into per-member
          // lastAlert and (when the member is active) top-level
          // lastAlert. The pre-cycle-61 implementation kept the
          // previous cached row when `prev.lastAlert.id !== a2.id`,
          // which silently dropped the frame on any device that had a
          // stale earlier alert sitting in the cache (different prior
          // episode, or a stale row from a session before this one
          // connected). The receiving device must be able to learn
          // about an alert for the first time from a snooze/silence
          // frame — useAlarmEpisode derives episode state from this
          // row.
          //
          // Snooze + silence both imply acknowledge server-side, so
          // the alert now carries acknowledged_at; we mirror the
          // unackCount bookkeeping used by alert_ack so the badge
          // stays correct. The "was counted" check intentionally only
          // decrements when the same id WAS already in the cache —
          // a first-time snooze/silence for an alert this device
          // hadn't seen never bumped unackCount in the first place,
          // so there's nothing to decrement.
          const a2 = f.alert;
          const prev = s.byMember[f.member_id] ?? emptyMemberState;
          const wasCounted =
            prev.lastAlert?.id === a2.id &&
            prev.lastAlert?.acknowledged_at == null;
          const byMember = {
            ...s.byMember,
            [f.member_id]: {
              ...prev,
              ...noteAlertRow(prev, a2),
              lastAlert: preferSoundingRow(prev.lastAlert, a2),
              unackCount: Math.max(
                0,
                prev.unackCount - (wasCounted ? 1 : 0),
              ),
              hasUnackedUrgent: wasCounted ? false : prev.hasUnackedUrgent,
            },
          };
          const isActiveMember =
            !s.activeMemberId || f.member_id === s.activeMemberId;
          return {
            ...s,
            byMember,
            lastAlert: isActiveMember
              ? preferSoundingRow(s.lastAlert, a2)
              : s.lastAlert,
            unackCount: Math.max(0, s.unackCount - (wasCounted ? 1 : 0)),
            // Silence drops the urgent-active flag the same way an
            // ack does (audio off). Snooze also drops it — the
            // cycle-20.x episode bridge in useAlarmSounds keys off
            // the derived `audioOn`, which goes false during the
            // snooze window.
            urgentActive: isActiveMember ? false : s.urgentActive,
          };
        }
        case "alert_treating": {
          // Cycle 391: a treating window opened, was extended, or was
          // TORN DOWN. Upsert unconditionally, exactly like the cycle-61
          // snooze/silence fix — a device must be able to learn about an
          // alert for the first time from this frame, and the teardown
          // frame is how every device re-arms together after a
          // breakthrough.
          //
          // THE ACK BOOKKEEPING RUNS BOTH WAYS, and that is the point:
          // opening a window writes `acknowledged_at` (treating implies
          // acknowledge), while a breakthrough CLEARS it — the broken
          // declaration re-arms the episode — so the badge has to be able
          // to count back up, not only down.
          const a2 = f.alert;
          const prev = s.byMember[f.member_id] ?? emptyMemberState;
          const sameRow = prev.lastAlert?.id === a2.id;
          const wasUnacked = sameRow && prev.lastAlert?.acknowledged_at == null;
          const nowUnacked = a2.acknowledged_at == null;
          const delta = sameRow
            ? (wasUnacked ? 0 : 1) - (nowUnacked ? 0 : 1)
            : 0;
          const byMember = {
            ...s.byMember,
            [f.member_id]: {
              ...prev,
              ...noteAlertRow(prev, a2),
              lastAlert: preferSoundingRow(prev.lastAlert, a2),
              unackCount: Math.max(0, prev.unackCount + delta),
            },
          };
          const isActiveMember =
            !s.activeMemberId || f.member_id === s.activeMemberId;
          // An OPEN window drops the urgent-active flag the same way a
          // snooze does. A TEARDOWN leaves it alone rather than setting
          // it true: `useAlarmEpisode` re-derives the live state from the
          // refreshed row on the same render, and inventing an urgent
          // flag here from a frame that might be a `recovered` teardown
          // would light a surface for an episode that just ended.
          const windowOpen =
            a2.custody_until != null &&
            new Date(a2.custody_until).getTime() > Date.now();
          // Cycle 407: a TEARDOWN frame names its reason. Keep it (for the
          // active member only) so the returned alarm can say WHY it is
          // back — "the member dropped to 44 — resuming alerts" — instead of
          // swapping surfaces silently. Recovery / handled-in-app carry a
          // reason too but re-arm nothing; the line-composer maps those
          // to no line.
          const handBack: CustodyHandBack | null =
            isActiveMember &&
            !catchUp &&
            a2.custody_until == null &&
            a2.custody_end_reason
              ? {
                  alertId: a2.id,
                  memberId: f.member_id,
                  reason: a2.custody_end_reason,
                  // SERVER-stamped only — the reading the server acted on.
                  // No client-cached fallback: an asserted number in a
                  // hand-back line comes from the server or is not said.
                  sgv: a2.custody_end_sgv ?? null,
                  atMs: Date.now(),
                }
              : s.custodyHandBack;
          return {
            ...s,
            byMember,
            lastAlert: isActiveMember
              ? preferSoundingRow(s.lastAlert, a2)
              : s.lastAlert,
            unackCount: Math.max(0, s.unackCount + delta),
            urgentActive:
              isActiveMember && windowOpen ? false : s.urgentActive,
            custodyHandBack: handBack,
          };
        }
        case "prediction_ack": {
          // Mirror to top-level only for the active member and only
          // when the id matches what we're currently displaying.
          if (s.activeMemberId && f.member_id !== s.activeMemberId) {
            return s;
          }
          if (s.lastPrediction?.id !== f.prediction.id) {
            return s;
          }
          return { ...s, lastPrediction: f.prediction };
        }
        case "member_update": {
          const dashboardCards = applyMemberToCards(
            s.dashboardCards,
            f.member,
          );
          // Drop the per-member entry when the row is archived so a
          // late-arriving frame doesn't resurrect it.
          if (f.member.archived_at != null && s.byMember[f.member.id]) {
            const next = { ...s.byMember };
            delete next[f.member.id];
            return { ...s, dashboardCards, byMember: next };
          }
          return { ...s, dashboardCards };
        }
        case "settings_update":
          // Pure side-effect for the settings context; the reducer
          // holds no settings state itself.
          return s;
        case "active_member_changed":
          // Pure side-effect; the parent calls useActiveMember.refresh()
          // which re-triggers the set-active flow.
          return s;
        case "reminder_fired": {
          // Cycle 76: the overlay + TTS are driven by the onReminderFired
          // callback. Cycle 362: ALSO track the durable occurrence so the
          // reminder survives the overlay's 10-minute auto-dismiss as a
          // "missed" record until someone acknowledges it.
          if (f.fire == null) return s; // /test fire or pre-362 server
          const fire = f.fire;
          const rest = s.outstandingReminders.filter((o) => o.id !== fire.id);
          return { ...s, outstandingReminders: [fire, ...rest] };
        }
        case "voice_call_placed": {
          // Cycle 381: record the DEADLINE. Anything unparseable is ignored
          // rather than defaulted, because a bad value must never be able to
          // silence a phone — the fail-open direction is "keep sounding".
          const until = Date.parse(f.quiet_until);
          if (!Number.isFinite(until) || !f.member_id) return s;
          return {
            ...s,
            voiceCallQuietUntilByMember: {
              ...s.voiceCallQuietUntilByMember,
              [f.member_id]: until,
            },
          };
        }
        case "custody_checkin": {
          // Cycle 407: the check-in edge. Two jobs, both narrow:
          //   1. patch the CACHED row's question/deadline for that member
          //      (the `alert_treating` frame that precedes this one has
          //      normally done it already; this is the belt), and
          //   2. for the ACTIVE member, emit the event the treating screen
          //      speaks on. Never for another member — a question about
          //      the member is never spoken over Ben's screen.
          // Nothing here sounds or silences anything; the row is the
          // state and the server is the arbiter.
          const prev = s.byMember[f.member_id];
          const patched =
            prev?.lastAlert && prev.lastAlert.id === f.alert_id
              ? {
                  ...prev.lastAlert,
                  custody_prompt_kind: f.question,
                  custody_prompt_deadline: f.expires_at,
                }
              : null;
          const byMember = patched
            ? {
                ...s.byMember,
                [f.member_id]: { ...prev!, lastAlert: patched },
              }
            : s.byMember;
          const isActiveMember =
            !s.activeMemberId || f.member_id === s.activeMemberId;
          const topAlert =
            isActiveMember && s.lastAlert?.id === f.alert_id
              ? {
                  ...s.lastAlert,
                  custody_prompt_kind: f.question,
                  custody_prompt_deadline: f.expires_at,
                }
              : s.lastAlert;
          return {
            ...s,
            byMember,
            lastAlert: topAlert,
            // In-STATE sequence (the cycle-112 `eventSeq` shape) — a pure
            // reducer, so StrictMode's double invocation yields the same
            // seq for the same frame and nothing re-cues.
            // Cycle 429: a backlog mints no event (the row still carries
            // the prompt; the deadline countdown is state).
            eventSeq: isActiveMember && !catchUp ? s.eventSeq + 1 : s.eventSeq,
            lastCheckinEvent: isActiveMember && !catchUp
              ? {
                  alertId: f.alert_id,
                  memberId: f.member_id,
                  question: f.question,
                  expiresAt: f.expires_at,
                  seq: s.eventSeq + 1,
                }
              : s.lastCheckinEvent,
          };
        }
        case "call_quiet": {
          // Cycle 403: record the DEADLINE for this ACCOUNT. Unparseable
          // is ignored rather than defaulted, on the cycle-381 rule: a
          // bad value must never be able to silence a phone, and the
          // fail-open direction is "keep sounding".
          //
          // ⚠ AN END FRAME IS THE SAME TYPE WITH `until` ALREADY PAST, so
          // it is stored exactly like an opening one and the single
          // `now < until` comparison downstream handles both. The entry is
          // deliberately NOT deleted on end: a stale past deadline is
          // already inert, and removing it would add a second code path
          // that could disagree with the comparison.
          const until = Date.parse(f.until);
          // Cycle 406: keyed by account AND member ("the member's quiet never
          // covers Ben"); a frame missing either half is ignored — audible.
          const key = callQuietKey(f.user_id, f.member_id);
          if (!Number.isFinite(until) || !key) return s;
          return {
            ...s,
            callQuietUntilByUser: {
              ...s.callQuietUntilByUser,
              [key]: until,
            },
          };
        }
        case "reminders_changed":
          // Cycle 217: pure side-effect (the Reminders pane reloads via
          // the onRemindersChanged callback); no reducer state.
          return s;
        case "hero_roster_changed":
          // Cycle 457: pure side-effect (lib/heroRosterSignal); no state.
          return s;
        case "reminder_snoozed":
          // Cycle 359: DORMANT. The reminder Snooze was removed; a lingering
          // old client may still emit this frame, so it stays in the union but
          // is ignored (no re-show). No reducer state.
          return s;
        case "reminder_dismissed": {
          // Cycle 272: the overlay hook applies the cross-device dismiss via
          // its callback. Cycle 362: also drop the outstanding record. Prefer
          // the precise `fire_id`; fall back to clearing every occurrence of
          // that reminder when an older server (or a test-fire dismiss) sends
          // no fire id.
          const before = s.outstandingReminders;
          const next =
            f.fire_id != null
              ? before.filter((o) => o.id !== f.fire_id)
              : before.filter((o) => o.reminder_id !== f.reminder_id);
          if (next.length === before.length) return s;
          return { ...s, outstandingReminders: next };
        }
        case "admin_broadcast": {
          // Cycle 97: operator banner (not member-scoped). A cleared
          // frame removes the active one (when ids match, or defensively
          // any active one); otherwise it becomes the active broadcast.
          if (f.cleared) {
            if (!s.activeBroadcast || s.activeBroadcast.id === f.id) {
              return { ...s, activeBroadcast: null };
            }
            return s;
          }
          return {
            ...s,
            activeBroadcast: {
              id: f.id,
              message: f.message ?? "",
              level: f.level ?? "info",
              expires_at: f.expires_at ?? null,
            } as ActiveBroadcast,
          };
        }
        case "status":
          return s;
        case "pong":
          return s;
        default:
          return s;
      }
    }
    default:
      return s;
  }
}

export interface GlucoseSocketState extends State {
  acknowledgeUrgent: () => void;
  /** Cycle 88: active-member-scoped CGM credential-failure flag (derived
   *  from `cgmAuthFailedByMember[activeMemberId]`). Drives the "update
   *  credentials" modal — a frame for a non-active member updates the
   *  per-member map but not this. */
  cgmAuthFailed: boolean;
  /** Cycle 468: the ACTIVE member's monitoring is suspended by its owner's
   *  subscription lock (a followed member, or this account's own). */
  serviceLocked: boolean;
  /** Cycle 101: optimistically insert a client-authored treatment into a
   *  member's cache (voice insulin logging — IOB ring starts instantly). */
  injectTreatment: (memberId: string, treatment: Treatment) => void;
  /** Cycle 101: drop an optimistic treatment on reconcile (the real
   *  server row arrives via the `treatment` frame under its own id). */
  forgetTreatment: (memberId: string, id: string) => void;
}

export interface GlucoseSocketCallbacks {
  /** Cycle 24: a remote PATCH /api/settings broadcast settings_update.
   *  Plumb into `settingsContext.applyRemoteUpdate`. */
  onSettingsUpdate?: (settings: UserSettings) => void;
  /** Cycle 24: a remote PUT /api/me/active_member broadcast
   *  active_member_changed. Plumb into `useActiveMember.refresh()`. */
  onActiveMemberChanged?: (memberId: string) => void;
  /** Cycle 24: a remote member create/patch/archive broadcast
   *  member_update. Plumb into `useActiveMember.refresh()` so a rename
   *  of the active member updates the header without a reload. */
  onMemberUpdate?: (member: Member) => void;
  /** Cycle 76: a scheduled reminder fired. Plumb into the ReminderToast
   *  state (and kiosk TTS). Side-effect only — no reducer state. */
  onReminderFired?: (reminder: Reminder, fire?: ReminderFire | null) => void;
  /** Cycle 217: a reminder was created/edited/deleted on another of the
   *  owner's devices. Plumb into a reload of the Settings → Reminders
   *  pane so the change appears instantly. Side-effect only. */
  onRemindersChanged?: () => void;
  /** Cycle 272: a FIRED reminder was marked Done / Closed on one of the
   *  owner's other devices. Clear the overlay here too. (Cycle 359 removed the
   *  reminder Snooze; the `reminder_snoozed` frame is now ignored — no
   *  `onReminderSnoozed` callback.) */
  onReminderDismissed?: (reminderId: string, fireId?: string | null) => void;
  /** Cycle 192: the published theme catalog changed (a global re-fetch
   *  nudge). Plumb into `useThemePacks().refresh()` so the picker + render
   *  path pick up the change live. Side-effect only — no reducer state. */
  onThemeCatalogUpdated?: () => void;
}

/** Open `WS /ws/glucose?token=…` and expose live state. Pass null to no-op. */
/** Cycle 429: the per-frame side-effect dispatch (settings context, active-
 *  member refresh, reminders, theme catalog) — ONE copy. Before this cycle
 *  the wake path rebuilt the socket with a second, drifted copy of this
 *  wiring (it had lost `reminders_changed` and `theme_catalog_updated`);
 *  now there is one socket and it is reopened, never rebuilt. */
function fireFrameCallbacks(
  cb: GlucoseSocketCallbacks | undefined,
  frame: WSFrame,
): void {
  // Cycle 457: a picture change reaches every subscriber, callbacks or not.
  if (frame.type === "hero_roster_changed") {
    notifyHeroRosterChanged(frame.member_id ?? null);
    return;
  }
  if (!cb) return;
  if (frame.type === "settings_update") {
    cb.onSettingsUpdate?.(frame.settings);
  } else if (frame.type === "active_member_changed") {
    cb.onActiveMemberChanged?.(frame.member_id);
  } else if (frame.type === "member_update") {
    cb.onMemberUpdate?.(frame.member);
  } else if (frame.type === "reminder_fired") {
    cb.onReminderFired?.(frame.reminder, frame.fire ?? null);
  } else if (frame.type === "reminders_changed") {
    cb.onRemindersChanged?.();
  } else if (frame.type === "reminder_snoozed") {
    // Cycle 359: dormant — the reminder Snooze was removed; ignore.
  } else if (frame.type === "reminder_dismissed") {
    cb.onReminderDismissed?.(frame.reminder_id, frame.fire_id ?? null);
  } else if (frame.type === "theme_catalog_updated") {
    cb.onThemeCatalogUpdated?.();
  }
}

export function useGlucoseSocket(
  token: string | null,
  activeMemberId: string | null = null,
  callbacks?: GlucoseSocketCallbacks,
): GlucoseSocketState {
  const [state, dispatch] = useReducer(reduce, initialState);
  const sockRef = useRef<GlucoseSocket | null>(null);
  // Cycle 435: the member a reseed is started FOR, read at reseed start
  // (never at reduce time) so a late answer can be attributed correctly.
  const activeMemberIdRef = useRef<string | null>(activeMemberId);
  activeMemberIdRef.current = activeMemberId;
  // Cycle 435: the ACCOUNT behind the token — its `sub`, or the raw token
  // for a kiosk device credential, which never rotates. A token ROTATION
  // keeps the subject; only a sign-in as a different account changes it.
  const accountRef = useRef<string | null>(null);
  // Cycle 433: the transport can ask for a REST reseed (the reading-age
  // bound). It is created below and lives in a ref so the socket effect
  // keeps its `[token]` dependency — re-creating the socket whenever a
  // callback identity changed is exactly the churn cycle 429 removed.
  const runReseedRef = useRef<(() => void) | null>(null);
  // Cycle 474 (B5): the last socket-driven session refresh, so a door that
  // keeps refusing cannot turn into a refresh storm.
  const lastSocketRefreshAtRef = useRef(Number.NEGATIVE_INFINITY);
  // Stash callbacks in a ref so the WS subscription doesn't tear down
  // whenever a parent recreates its handlers.
  const callbacksRef = useRef<GlucoseSocketCallbacks | undefined>(callbacks);
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  useEffect(() => {
    if (!token) {
      dispatch({ type: "status", status: "idle" });
      return;
    }
    // ⚠ Cycle 435 (security review): `/login` is routed inside the mounted
    // shell, so signing in as a DIFFERENT account only changes the token
    // here — the previous account's per-member readings, cards and (now)
    // confirmations would otherwise survive until the new snapshot lands,
    // and a surviving confirmation would render them as CURRENT for up to
    // the unknown bound. Keyed on the token's account subject, never on
    // the token string: a refresh rotation rebuilds this socket routinely
    // and must not blank a live episode's state.
    const account = refreshIdentity(token)?.split("|")[0] ?? token;
    if (accountRef.current != null && accountRef.current !== account) {
      dispatch({ type: "reset-account" });
    }
    accountRef.current = account;
    const s = new GlucoseSocket(token, {
      // Cycle 429: frames arrive in BATCHES with their delivery meta; the
      // side-effect callbacks fire per frame as before, the reducer gets
      // the batch so a backlog is applied as state.
      onFrames: (frames, meta) => {
        for (const frame of frames) {
          fireFrameCallbacks(callbacksRef.current, frame);
        }
        dispatch({ type: "frames", frames, meta });
        // Cycle 474: a SNAPSHOT is the server's confirmation of the whole
        // state; on a socket created after the settle began it ends the
        // settle (lib/appLifecycle). Dispatched first, so the settle-end
        // re-evaluation renders against the snapshot's state.
        if (meta.connectedAtMs != null && frames.some((f) => f.type === "snapshot")) {
          markVerifiedFresh(meta.connectedAtMs);
        }
      },
      onStatus: (status) => dispatch({ type: "status", status }),
      // Cycle 433: the reading-age bound reconnects AND asks.
      onReseedNeeded: () => runReseedRef.current?.(),
      // Cycle 474 (B5): the bearer is read at every (re)open, so a refreshed
      // token reaches the very next attempt — never the one captured here.
      currentToken: () => getBearer(),
      // Cycle 474 (B5): the socket closed before it opened. If that can be the
      // door refusing the bearer (a 1008, or any pre-open close while the
      // token is expired or about to be — a browser reports the server's
      // pre-accept refusal as 1006), refresh the session, rate-limited, and
      // reconnect at once on success. Never a sign-out (cycle 235.2).
      onOpenRefused: (code) => {
        if (!shouldRefreshAfterRefusedOpen(code, accessTokenExpiresWithinMs(TOKEN_REFRESH_LEAD_MS))) return;
        const now = Date.now();
        if (now - lastSocketRefreshAtRef.current < SOCKET_REFRESH_MIN_GAP_MS) return;
        lastSocketRefreshAtRef.current = now;
        void refreshSessionForSocket().then((outcome) => {
          if (outcome === "ok") s.reopen("manual");
        });
      },
    });
    sockRef.current = s;
    s.start();
    return () => {
      s.stop();
      sockRef.current = null;
      // Cycle 112: drop live-fire events on teardown so a stale event
      // can't fire a sound after the socket closes.
      dispatch({ type: "clear-events" });
    };
  }, [token]);

  // Re-seed active-scoped state via REST. Shared between the
  // member-switch effect and the cycle-33 wake-refresh hook so a
  // tab-foreground or Capacitor `appStateChange` event refills the
  // Now screen without waiting on the next push.
  //
  // ⚠ CYCLE 433 — THE ABORT IS THE CALLER'S, AND IT IS NOT OPTIONAL. The
  // caller owns an `AbortController`; every read here carries its signal,
  // and an abort surfaces as an ordinary failed `ApiResult`, never a
  // throw. Before this cycle the cancellation token was a plain
  // `{ cancelled }` object: it stopped the DISPATCH but not the fetch, so
  // a request iOS had frozen mid-flight kept the `await` below pending —
  // and with it the wake handler's in-flight guard. That is the 88 seconds
  // of 2026-09-13.
  const reseedFromRest = useCallback(
    async (ctrl: AbortController) => {
      const signal = ctrl.signal;
      // ⚠ Cycle 435 (security review): the member this reseed is FOR is
      // captured at the start and rides on the seed actions, so an answer
      // that lands after a member switch is dropped by the reducer rather
      // than attributed — and confirmed — under the new member.
      const forMember = activeMemberIdRef.current;
      // ⚠ Cycle 435 (security re-run): the ACCOUNT this reseed was started
      // under. A member id is only unique within an account, so the member
      // tag above cannot catch an answer fetched with the PREVIOUS
      // account's bearer that lands after a sign-in as someone else — and
      // `seed-dashboard` carries no member at all. Nothing is dispatched
      // once the account has changed; the new account's own reseed is
      // already on its way.
      const forAccount = accountRef.current;
      const startedAt = Date.now();
      const [latest, hist, dash, alerts] = await Promise.all([
        glucoseApi.latest(signal),
        glucoseApi.history(3, READINGS_CAP, signal),
        membersApi.dashboard(signal),
        // Cycle 429: the active member's recent alert rows, so the slot is
        // rebound from server truth on every wake — the reseed used to
        // refresh the READING and leave the alert slot where a dead socket
        // had left it. Twenty-four hours, not three: an urgent-low row has
        // stayed open for three hours in production (2026-09-08), and a
        // window that misses it would drop a live binding.
        alertsApi.list(24, 50, false, signal),
      ]);
      // ⚠ An ABORTED reseed dispatches NOTHING. `seed` with a null reading
      // clears the hero, and an abort is not evidence that there is no
      // reading — it is evidence that we did not get to ask.
      if (signal.aborted) return;
      if (accountRef.current !== forAccount) return;
      if (alerts.ok) {
        dispatch({ type: "seed-alerts", alerts: alerts.data.alerts, memberId: forMember });
      }
      // ⚠ Cycle 474 (B8): a reading call that FAILED (network, timeout, a
      // transient refresh) is NOT the server saying "no reading" — it is no
      // answer at all. Dispatching it as a seed wrote `lastReading: null`
      // and wiped the held hero to No Data after a sleep, with nothing to
      // retry it until a restart. Now the reading seed is skipped (the held
      // reading stands; its own staleness still judges it — stale is
      // neutral), and the reseed is retried a bounded number of times while
      // visible (below, after the dashboard seed).
      if (latest.ok) {
        reseedFailuresRef.current = 0;
        // Cycle 474: the server answered — the settle after a resume ends.
        markVerifiedFresh(startedAt);
        const reading = latest.ok ? latest.data.reading : null;
        // /api/glucose/history returns newest-first, but recentReadings
        // is contractually chronological (oldest-first) — the WS reducer
        // appends new frames to the end of the array. Sort the seed so
        // delta math (NowScreen: recent[last] − recent[last-1]) compares
        // the actual prior 5-min reading instead of comparing the latest
        // to whatever happened to be the oldest entry.
        const recent = hist.ok
          ? [...hist.data.readings].sort(
              (x, y) =>
                new Date(x.timestamp).getTime() -
                new Date(y.timestamp).getTime(),
            )
          : reading
            ? [reading]
            : [];
        // Cycle 433: the reading call ANSWERING is the confirmation — even
        // when the answer carries the same reading we already had, or no
        // reading at all. What it proves is that this client is still being
        // told what the newest reading is. A failed `latest` proves nothing
        // and leaves the clock where it was.
        dispatch({
          type: "seed",
          reading,
          recent,
          confirmedAtMs: latest.ok ? Date.now() : null,
          // Cycle 435: the server's verdict rides with the reading it judged.
          // `/api/glucose/latest` answers `stale: true, age_seconds: null` for
          // "no reading" — a confirmed empty, not an absent one.
          verdict: latest.ok
            ? { stale: latest.data.stale, ageSeconds: latest.data.age_seconds ?? null }
            : null,
          memberId: forMember,
        });
        if (latest.ok) sockRef.current?.noteReadingConfirmed();
      }
      if (dash.ok) {
        dispatch({
          type: "seed-dashboard",
          cards: dash.data.members.map(dashboardCardFromMember),
        });
      }
      if (!latest.ok) scheduleReseedRetryRef.current?.();
    },
    [],
  );

  // ─── Cycle 433: the reseed is BOUNDED and the guard always releases ──
  //
  // The rules live in `lib/wakeReseed.ts` — with no React in them, so they
  // can be driven under node against a promise that never settles, which is
  // exactly what a fetch iOS froze mid-flight is. Here we only wire them.
  const reseedRunnerRef = useRef<ReseedRunner | null>(null);
  // Cycle 474 (B8/B6): a failed reseed is retried — at most
  // RESEED_RETRY_MAX times in a row, RESEED_RETRY_MS apart, only while
  // visible; any answered reseed resets the count. Through the runner, so
  // it coalesces with every wake and is bounded like them.
  const reseedFailuresRef = useRef(0);
  const reseedRetryTimerRef = useRef<number | null>(null);
  const scheduleReseedRetryRef = useRef<(() => void) | null>(null);
  scheduleReseedRetryRef.current = () => {
    if (reseedRetryTimerRef.current != null) return;
    if (reseedFailuresRef.current >= RESEED_RETRY_MAX) return;
    reseedFailuresRef.current += 1;
    reseedRetryTimerRef.current = window.setTimeout(() => {
      reseedRetryTimerRef.current = null;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      runReseedRef.current?.();
    }, RESEED_RETRY_MS);
  };
  useEffect(
    () => () => {
      if (reseedRetryTimerRef.current != null) window.clearTimeout(reseedRetryTimerRef.current);
      reseedRetryTimerRef.current = null;
    },
    [],
  );
  useEffect(() => {
    const runner = createReseedRunner({
      reseed: reseedFromRest,
      timeoutMs: RESEED_TIMEOUT_MS,
      onTimeout: noteReseedTimeout,
    });
    reseedRunnerRef.current = runner;
    runReseedRef.current = () => runner.request();
    return () => {
      // Unmount is one of the paths the guard must release on: `dispose`
      // aborts anything in flight, which settles its `await`, whose
      // `finally` clears the marker.
      runner.dispose();
      reseedRunnerRef.current = null;
      runReseedRef.current = null;
    };
  }, [reseedFromRest]);

  // Active-member side effects: clear active-scoped state, re-seed
  // active stream via REST, and re-seed dashboard cards so the
  // dashboard isn't blank for several seconds while we wait for the
  // next push.
  useEffect(() => {
    dispatch({ type: "set-active", memberId: activeMemberId });
    if (!token || !activeMemberId) return;
    // Cycle 433: bounded like every other reseed. This one holds no guard,
    // but an unbounded fetch still pins a connection and leaves the
    // confirmation clock un-renewed for as long as iOS keeps it frozen.
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), RESEED_TIMEOUT_MS);
    void reseedFromRest(ctrl).finally(() => clearTimeout(timer));
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [token, activeMemberId, reseedFromRest]);

  // ─── Cycle 33: wake-refresh ──────────────────────────────────────
  // Phones suspend the WebSocket when the app goes to background. On
  // return-to-foreground we (a) re-seed via REST so the user sees the
  // current sgv immediately and (b) reopen the socket if its
  // readyState isn't OPEN/CONNECTING so subsequent pushes don't sit
  // queued on a half-closed connection.
  //
  // Two triggers, both routed through the same handler:
  //   1. `document.visibilitychange` → fires on every variant (tab
  //      switch in a browser, lock/unlock on iOS Safari, etc.).
  //   2. Capacitor `App.appStateChange` → fires on the native shell's
  //      OS-level resume even when the webview's visibility didn't
  //      change. Pulled in dynamically via the same pattern as
  //      `lib/capacitor.ts`; non-native surfaces never execute it.
  //
  // ⚠ CYCLE 433 — A WAKE IS NEVER SWALLOWED BY A RESEED. Until this cycle
  // the handler's FIRST line was `if (wakeInFlightRef.current) return`, and
  // the marker was released in a `finally` that could only run once the
  // `await reseedFromRest(...)` settled. iOS freezes a fetch mid-flight
  // when the app backgrounds and the promise pends for MINUTES after the
  // app returns, so every wake that arrived in that window was discarded —
  // reconnect included. On 2026-09-13 the 23:14:50 wake reached the server
  // at 23:23:06 with no reopen, no reseed and no socket behind it, and the
  // phone displayed a value it had stopped receiving for the whole of it.
  //
  // The handler is now SYNCHRONOUS and the two halves are separated
  // (`createWakeHandler`, `lib/wakeReseed.ts`):
  //   • the RECONNECT is unconditional and behind no guard at all — it is
  //     one synchronous call that cannot pend;
  //   • the RESEED goes through the bounded, coalescing runner.
  // The 1.5 s minimum gap survives as what it always was — a rate limit on
  // tab-switching — but it is now measured from the START of an acted-on
  // wake rather than from the completion of the last one, so a slow reseed
  // can no longer widen it into an unbounded hole.

  useEffect(() => {
    if (!token) return;
    const wakeHandler = createWakeHandler({
      now: () => Date.now(),
      minGapMs: WAKE_MIN_GAP_MS,
      // ⚠ CYCLE 429 — WAKE MEANS RECONNECT, UNCONDITIONALLY. The socket
      // is closed and reopened whatever its `readyState` says: a socket
      // that survived a freeze is exactly the one that lies (OPEN, dead).
      // Its snapshot is the seed; frames the old socket still had queued
      // are dropped by generation. Then the REST reseed (reading, history,
      // dashboard AND the alert rows) converges the rest.
      reopen: () => {
        // Cycle 474 (B5): a token about to expire is refreshed alongside —
        // NEVER in front of — the reopen (the cycle-433 rule: the reconnect
        // is behind no guard). A 1008 on the reopen asks again, coalesced.
        if (
          accessTokenExpiresWithinMs(TOKEN_REFRESH_LEAD_MS) &&
          Date.now() - lastSocketRefreshAtRef.current >= SOCKET_REFRESH_MIN_GAP_MS
        ) {
          lastSocketRefreshAtRef.current = Date.now();
          void refreshSessionForSocket().then((o) => {
            if (o === "ok") sockRef.current?.reopen("manual");
          });
        }
        sockRef.current?.reopen("wake");
      },
      reseed: () => {
        reseedFailuresRef.current = 0;
        if (activeMemberId) reseedRunnerRef.current?.request();
      },
    });
    const handleWake = () => wakeHandler.wake();
    // Cycle 474 (B6): the network coming back is a wake too — nothing else
    // retried a reseed or a refresh that failed while it was down.
    const onOnline = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      handleWake();
    };

    const onVisibility = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "visible") {
        handleWake();
      }
    };

    // Cycle 429: `pageshow` too — a page restored from the back/forward
    // cache fires it without a visibility change. Only the RESTORE
    // (`persisted`): the initial load fires `pageshow` as well, and
    // reopening the socket that just opened would be noise.
    const onPageShow = (ev: Event) => {
      if ((ev as PageTransitionEvent).persisted) handleWake();
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("pageshow", onPageShow);
      window.addEventListener("online", onOnline);
    }

    // Capacitor App.appStateChange — via the bridge-injected
    // `window.Capacitor.Plugins` global (the pure-remote webview can't
    // resolve a bare `import("@capacitor/app")` at runtime).
    let appListenerHandle: { remove: () => Promise<void> } | null = null;
    let appListenerCancelled = false;
    if (isCapacitorNative()) {
      (async () => {
        try {
          const appProxy = (
            window as {
              Capacitor?: {
                Plugins?: {
                  App?: {
                    addListener: (
                      event: string,
                      cb: (state: { isActive: boolean }) => void,
                    ) => Promise<{ remove: () => Promise<void> }>;
                  };
                };
              };
            }
          ).Capacitor?.Plugins?.App;
          if (appProxy == null) throw new Error("App plugin not on bridge");
          if (appListenerCancelled) return;
          const handle = await appProxy.addListener(
            "appStateChange",
            (state: { isActive: boolean }) => {
              if (state?.isActive) {
                // Cycle 474: the shell's own resume signal (iOS can skip
                // `visibilitychange` on a return from the lock screen)
                // starts the settle as well as the wake.
                noteAppActive();
                handleWake();
              }
            },
          );
          if (appListenerCancelled) {
            void handle.remove();
            return;
          }
          appListenerHandle = handle;
        } catch (err) {
          // Non-native or plugin not bundled — silently skip.
          console.warn("[glucose-socket] appStateChange listener failed", err);
        }
      })();
    }

    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("pageshow", onPageShow);
        window.removeEventListener("online", onOnline);
      }
      appListenerCancelled = true;
      if (appListenerHandle) {
        void appListenerHandle.remove();
        appListenerHandle = null;
      }
    };
  }, [token, activeMemberId]);

  // ⚠ CYCLE 434 — the reading-age bound is DERIVED FROM THE ACTIVE
  // MEMBER'S OWN FEED, not from one constant. `recentReadings` is already
  // scoped to the active member by the reducer, so switching member
  // re-derives from that member's gaps and falls back to the constant
  // until enough of them are seen. Pure: same readings in, same bound out.
  //
  // ⚠ "PER MEMBER" MEANS PER *ACTIVE* MEMBER. `readingConfirmedAtMs` is a
  // transport-wide fact (cycle 433's design — the question is whether THIS
  // DEVICE is still being told, not whose sensor it is), so a household
  // surface applies the active member's bound to every row. A non-active
  // member on a faster feed therefore inherits a wider bound — bounded in
  // turn by that row's own `isStale(reading.timestamp, now, provider)`, so
  // no row can outlive the server's rule.
  const readingBoundMs = useMemo(
    () => deriveReadingBoundMs(state.recentReadings.map((r) => Date.parse(r.timestamp))),
    [state.recentReadings],
  );

  // The transport judges reopen against the same bound the display judges
  // "can I still show this number" against, so the client can never blank
  // a number before it has even gone to ask whether a newer one exists.
  useEffect(() => {
    sockRef.current?.setReadingBound(readingBoundMs);
  }, [readingBoundMs]);

  // Cycle 435: tell the transport how old the reading on screen is, so a
  // wake reopen can report `held_reading_age_s`. Telemetry only.
  useEffect(() => {
    sockRef.current?.noteHeldReading(readingTsMs(state.lastReading));
  }, [state.lastReading]);

  return useMemo(
    () => ({
      ...state,
      readingBoundMs,
      /** The age past which a reading may no longer be presented as
       *  current, for THIS member's feed. The bound plus one reseed
       *  allowance — see `lib/readingFreshness.ts` for why the reseed's
       *  full allowance is included rather than blanking at the bound. */
      readingUnknownAfterMs: readingBoundMs + RESEED_TIMEOUT_MS,
      acknowledgeUrgent: () => dispatch({ type: "ack-urgent" }),
      cgmAuthFailed: state.activeMemberId
        ? state.cgmAuthFailedByMember[state.activeMemberId] ?? false
        : false,
      serviceLocked: state.activeMemberId
        ? state.serviceLockedByMember[state.activeMemberId] ?? false
        : false,
      injectTreatment: (memberId: string, treatment: Treatment) =>
        dispatch({ type: "inject-treatment", memberId, treatment }),
      forgetTreatment: (memberId: string, id: string) =>
        dispatch({ type: "forget-treatment", memberId, id }),
    }),
    [state, readingBoundMs],
  );
}
